// DealerProductsPanel — which products a dealer may sell (Dealers → Products).
//
// "All products" = no restriction: users get every product their role allows.
// "Only selected" = users of this dealer get only the checked products, even if
// their role allows more (e.g. Marshall → Pleated Screens only, no Windows).
// Backed by GET/PUT /dealers/<id>/products; the effective result for a user comes
// from /user-roles/my-features (FeaturesContext), loaded at login.

import { useEffect, useState } from 'react';
import {
    Radio, RadioGroup, FormControlLabel, Checkbox, Button, Alert,
} from '@mui/material';
import { authFetch, authFetchRequest } from './utils/api';

const DealerProductsPanel = ({ dealerId, dealerName }) => {
    const [products, setProducts] = useState([]);
    const [mode, setMode] = useState('all');          // 'all' | 'selected'
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);     // { severity, text }

    useEffect(() => {
        setLoading(true);
        setMessage(null);
        authFetch(`/dealers/${dealerId}/products`)
            .then(data => {
                setProducts(data?.products || []);
                setMode(data?.restricted ? 'selected' : 'all');
                setSelected(data?.allowed || []);
            })
            .catch(err => setMessage({ severity: 'error', text: `Failed to load products: ${err.message}` }))
            .finally(() => setLoading(false));
    }, [dealerId]);

    const toggle = (code) => {
        setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
        setMessage(null);
    };

    const handleSave = async () => {
        if (mode === 'selected' && selected.length === 0) {
            setMessage({ severity: 'warning', text: 'Select at least one product, or choose "All products".' });
            return;
        }
        setSaving(true);
        try {
            const allowed = mode === 'all' ? [] : selected;
            await authFetchRequest(`/dealers/${dealerId}/products`, { allowed }, 'PUT');
            setMessage({
                severity: 'success',
                text: 'Saved. Users of this dealer see the change after they log in again or reload the page.',
            });
        } catch (err) {
            setMessage({ severity: 'error', text: `Save failed: ${err.message}` });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p>Loading products…</p>;

    return (
        <div style={{ maxWidth: 520 }}>
            <h4>Products for {dealerName}</h4>
            <RadioGroup value={mode} onChange={(e) => { setMode(e.target.value); setMessage(null); }}>
                <FormControlLabel value="all" control={<Radio size="small" />}
                    label="All products (whatever each user's role allows)" />
                <FormControlLabel value="selected" control={<Radio size="small" />}
                    label="Only selected products" />
            </RadioGroup>

            <div style={{ paddingLeft: 28, marginBottom: 12 }}>
                {products.map(p => (
                    <div key={p.code}>
                        <FormControlLabel
                            disabled={mode === 'all'}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={mode === 'all' || selected.includes(p.code)}
                                    onChange={() => toggle(p.code)}
                                />
                            }
                            label={p.coming_soon ? `${p.label} (coming soon)` : p.label}
                        />
                    </div>
                ))}
            </div>

            <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
                A product still has to be enabled for the user's role (Settings → Available Features).
                This setting can only take products away from this dealer, never add them.
            </p>

            {message && <Alert severity={message.severity} sx={{ mb: 1 }}>{message.text}</Alert>}

            <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
            </Button>
        </div>
    );
};

export default DealerProductsPanel;
